import React, { useEffect, useMemo, useState } from "react";
import ProjectData from "../data/letbabytalkData.json";
import { Contact } from "./contact";
import "./project.css";
import { smoothScrollToElement } from "../utils/scroll";

const navItems = [
  { label: "Overview", href: "#overview" },
  { label: "Prototype", href: "#prototype" },
  { label: "Features", href: "#features" },
  { label: "How It Helps", href: "#cases" },
  { label: "Contact", href: "#contact" },
];

const careFlow = [
  {
    label: "Record",
    text: "Capture a short sample of your baby's cry",
  },
  {
    label: "Analyze",
    text: "Analyze cry patterns and return a predicted result",
  },
  {
    label: "Guide",
    text: "Use the insights to decide what to try next",
  },
];

const heroHighlights = [
  { value: "Short Sample", label: "designed for quick recording" },
  { value: "Simple Review", label: "easy-to-read result" },
  { value: "Early Access", label: "iOS & Android" },
];

const getPrototypeImageUrl = (path) =>
  path
    ? path.startsWith("http")
      ? path
      : `${process.env.PUBLIC_URL}/${path.replace(/^\.\.{1,2}\//, "")}`
    : "";

const prototypeFallback = [
  {
    title: "Quick capture",
    text: "Tap record and capture a short cry sample instantly.",
    image: "/img/prototype/1.jpg",
  },
  {
    title: "AI analysis",
    text: "Analyze cry sound patterns and uncover likely needs.",
    image: "/img/prototype/2.jpg",
  },
  {
    title: "Clear result",
    text: "Show a simple, easy-to-read insight screen.",
    image: "/img/prototype/3.jpg",
  },
  {
    title: "Cry history",
    text: "Review past recordings and track changing patterns.",
    image: "/img/prototype/5.jpg",
  },
  {
    title: "Confidence meter",
    text: "See likelihood scores with clear, calm guidance.",
    image: "/img/prototype/6.jpg",
  },
  {
    title: "Everyday access",
    text: "Jump to history, settings, and follow-up support.",
    image: "/img/prototype/4.jpg",
  },
];

export const Project = (props) => {
  const data = props.data || ProjectData.letbabytalk || ProjectData;
  const [activeSection, setActiveSection] = useState("overview");
  const [currentPrototype, setCurrentPrototype] = useState(0);
  const prototypeSteps = useMemo(() => {
    const images = data?.prototype_images || prototypeFallback.map((item) => item.image);
    const titles = data?.prototype_descriptions || prototypeFallback.map((item) => item.title);
    const details = data?.prototype_additional_descriptions || prototypeFallback.map((item) => [item.text]);

    return images.map((image, index) => {
      const rawTitle = titles[index] || `Step ${index + 1}`;
      const title = rawTitle.replace(/^Step\s*\d+[:-]?\s*/i, "").trim();
      const navTitle = title.length > 28 ? `${title.slice(0, 28).trim()}…` : title;
      const detailPoints =
        details[index]
          ?.map((item) => item.replace(/^-+\s*/, "").trim())
          .filter(Boolean) || [];

      return {
        image: getPrototypeImageUrl(image),
        title,
        navTitle,
        detail:
          detailPoints[0] ||
          prototypeFallback[index]?.text ||
          "Explore the next LetBabyTalk screen.",
        detailPoints,
      };
    });
  }, [data]);

  useEffect(() => {
    if (!prototypeSteps.length) return;
    const interval = window.setInterval(() => {
      setCurrentPrototype((previous) => (previous + 1) % prototypeSteps.length);
    }, 5000);

    return () => window.clearInterval(interval);
  }, [prototypeSteps.length]);

  useEffect(() => {
    if (props.from === "homepage") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [props.from]);

  useEffect(() => {
    const sectionIds = [
      "overview",
      "prototype",
      "features",
      "cases",
      "contact",
    ];

    const updateActiveSection = () => {
      const navHeight = document.getElementById("menu")?.offsetHeight || 0;
      const readingLine = window.scrollY + navHeight + window.innerHeight * 0.28;
      let current = "overview";

      sectionIds.forEach((id) => {
        const section = document.getElementById(id);
        if (section && section.offsetTop <= readingLine) current = id;
      });

      setActiveSection(current);
    };

    updateActiveSection();
    window.addEventListener("scroll", updateActiveSection, { passive: true });
    window.addEventListener("resize", updateActiveSection);

    return () => {
      window.removeEventListener("scroll", updateActiveSection);
      window.removeEventListener("resize", updateActiveSection);
    };
  }, []);

  const showPreviousPrototype = () => {
    if (!prototypeSteps.length) return;
    setCurrentPrototype((previous) =>
      previous === 0 ? prototypeSteps.length - 1 : previous - 1
    );
  };

  const showNextPrototype = () => {
    if (!prototypeSteps.length) return;
    setCurrentPrototype((previous) => (previous + 1) % prototypeSteps.length);
  };

  const handleSectionNavClick = (event, id) => {
    const target = document.getElementById(id);
    if (!target) return;

    event.preventDefault();
    setActiveSection(id);
    smoothScrollToElement(id, { menuId: "menu" });
  };

  const heroScreens = useMemo(
    () =>
      [data?.prototype_images?.[2], data?.prototype_images?.[4]].filter(
        Boolean
      ),
    [data]
  );

  return (
    <div className="project-page">
      <nav id="menu" className="navbar navbar-default navbar-fixed-top project-nav">
        <div className="container">
          <div className="navbar-header">
            <button
              type="button"
              className="navbar-toggle collapsed"
              data-toggle="collapse"
              data-target="#project-navbar-collapse"
            >
              <span className="sr-only">Toggle navigation</span>
              <span className="icon-bar"></span>
              <span className="icon-bar"></span>
              <span className="icon-bar"></span>
            </button>

            <a className="navbar-brand page-scroll" href="#/">
              <img src="./img/cradleai_logo.png" alt="CradleAI Logo" />
            </a>
          </div>

          <div className="collapse navbar-collapse" id="project-navbar-collapse">
            <ul className="nav navbar-nav navbar-right">
              <li>
                <a href="#/" className="page-scroll">
                  Home
                </a>
              </li>
              {navItems.map((item) => {
                const id = item.href.replace("#", "");
                return (
                  <li
                    className={activeSection === id ? "active" : ""}
                    key={item.href}
                  >
                    <a
                      href={item.href}
                      className="page-scroll"
                      aria-current={activeSection === id ? "page" : undefined}
                      onClick={(event) => handleSectionNavClick(event, id)}
                    >
                      {item.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </nav>

      <header
        className="project-hero"
        id="project-top"
        style={{
          "--project-hero-image": `url(${process.env.PUBLIC_URL}/img/project/family-pic.png)`,
        }}
      >
        <div className="project-hero-backdrop" />
        <div className="project-hero-content project-container">
          <div className="project-hero-copy-block">
            <p className="project-kicker">CradleAI product</p>
            <h1>{data?.title || "LetBabyTalk"}</h1>
            <p className="project-hero-copy">
              {data?.paragraph ||
                "Understand baby cries with calm, explainable AI support."}
            </p>
            <div className="project-hero-actions">
              <a className="project-primary-action" href="#prototype">
                Try the Prototype
              </a>
              <a className="project-secondary-action" href="#download">
                Get Early Access
              </a>
            </div>
          </div>

          <div className="project-hero-showcase" aria-label="LetBabyTalk app preview">
            {heroScreens.map((screen, index) => (
              <div
                className={`project-hero-phone project-hero-phone-${index + 1}`}
                key={screen}
              >
                <span className="project-phone-speaker" aria-hidden="true" />
                <img
                  src={screen}
                  alt={`LetBabyTalk app preview ${index + 1}`}
                />
                <span className="project-phone-home" aria-hidden="true" />
              </div>
            ))}
          </div>
        </div>
      </header>

      <main>
        <section className="project-section project-intro-band" id="overview">
          <div className="project-container project-overview-grid">
            <div className="project-overview-copy">
              <p className="project-kicker">FOR EVERYDAY BABY CARE</p>
              <h2>How LetBabyTalk Works </h2>
              <p>
                Record a short cry sample, review possible patterns, and use simple guidance to decide what to try next
              </p>
            </div>

            <div className="project-overview-panel">
              <div className="project-flow-list" aria-label="LetBabyTalk care flow">
                {careFlow.map((step, index) => (
                  <article className="project-flow-step" key={step.label}>
                    <span>{`0${index + 1}`}</span>
                    <div>
                      <h3>{step.label}</h3>
                      <p>{step.text}</p>
                    </div>
                  </article>
                ))}
              </div>

              <div className="project-stat-list" aria-label="LetBabyTalk highlights">
                {heroHighlights.map((item) => (
                  <div className="project-stat" key={item.value}>
                    <strong>{item.value}</strong>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

          <section className="project-section project-prototype" id="prototype">
          <div className="project-container project-prototype-layout">
            <nav className="project-prototype-nav" aria-label="Prototype step menu">
              <ul className="project-prototype-nav-list">
                {prototypeSteps.map((step, index) => (
                  <li key={step.title}>
                    <button
                      type="button"
                      className={`project-prototype-nav-item ${index === currentPrototype ? "active" : ""}`}
                      onClick={() => setCurrentPrototype(index)}
                    >
                      <span className="project-prototype-nav-index">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="project-prototype-nav-name">
                        {step.navTitle}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="project-prototype-panel">
              <div className="project-prototype-carousel">
                <button
                  type="button"
                  className="project-prototype-arrow project-prototype-arrow-prev"
                  onClick={showPreviousPrototype}
                  aria-label="Show previous prototype screen"
                >
                  <span aria-hidden="true">&#8249;</span>
                </button>

                <div
                  className="project-prototype-carousel-track"
                  style={{ transform: `translateX(-${currentPrototype * 100}%)` }}
                >
                  {prototypeSteps.map((step) => (
                    <div className="project-prototype-carousel-item" key={step.title}>
                      <div className="project-prototype-phone">
                        <span className="project-device-speaker" aria-hidden="true" />
                        <img src={step.image} alt={step.title} />
                        <span className="project-device-home" aria-hidden="true" />
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  className="project-prototype-arrow project-prototype-arrow-next"
                  onClick={showNextPrototype}
                  aria-label="Show next prototype screen"
                >
                  <span aria-hidden="true">&#8250;</span>
                </button>
              </div>

              <div className="project-prototype-step-controls" aria-label="Prototype step navigation">
                {prototypeSteps.map((_, index) => (
                  <button
                    type="button"
                    key={index}
                    className={`project-prototype-step-dot ${index === currentPrototype ? "active" : ""}`}
                    onClick={() => setCurrentPrototype(index)}
                    aria-label={`Go to step ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            <div className="project-prototype-copy" key={currentPrototype}>
              <p className="project-kicker">Prototype</p>
              <h2>{prototypeSteps[currentPrototype]?.title}</h2>
              <p className="project-prototype-lede">
                {prototypeSteps[currentPrototype]?.detail}
              </p>
              {prototypeSteps[currentPrototype]?.detailPoints?.length > 1 ? (
                <div className="project-prototype-insights" aria-label="Prototype step details">
                  {prototypeSteps[currentPrototype].detailPoints
                    .slice(1)
                    .map((point) => (
                      <article className="project-prototype-insight" key={point}>
                        <span aria-hidden="true" />
                        <p>{point}</p>
                      </article>
                    ))}
                </div>
              ) : null}
              <div className="project-prototype-meta">
                <span className="project-prototype-step-counter">
                  {`Step ${currentPrototype + 1} / ${prototypeSteps.length}`}
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="project-section project-features" id="features">
          <div className="project-container">
            <div className="project-feature-showcase">
              <div className="project-feature-lead">
                <p className="project-kicker">Features</p>
                <h2>Gentle support for everyday baby care</h2>
                <p className="project-feature-lede">
                  LetBabyTalk helps parents capture short cry samples, review possible patterns, and track changes over time
                </p>

                <div className="project-feature-access" id="download">
                  <div className="project-feature-download">
                    <div className="project-store-links">
                      <a href={data?.google_link} target="_blank" rel="noopener noreferrer">
                        <span className="project-store-logo" aria-hidden="true">
                          <img src={`${process.env.PUBLIC_URL}/img/googleplay.png`} alt="" />
                        </span>
                        <span className="project-store-meta">
                          <small>Download on</small>
                          <strong>Google Play</strong>
                        </span>
                      </a>
                      <a href={data?.apple_link} target="_blank" rel="noopener noreferrer">
                        <span className="project-store-logo" aria-hidden="true">
                          <img src={`${process.env.PUBLIC_URL}/img/appstore.png`} alt="" />
                        </span>
                        <span className="project-store-meta">
                          <small>Download on</small>
                          <strong>App Store</strong>
                        </span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="project-feature-panel">
                <div className="project-feature-grid">
                  {data?.features?.map((feature, index) => (
                    <article className="project-feature-card" key={feature.title}>
                      <span>{String(index + 1).padStart(2, "0")}</span>
                      <div>
                        <h3>{feature.title}</h3>
                        <p>{feature.text}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="project-section project-cases" id="cases">
          <div className="project-container">
            <div className="project-section-head">
              <p className="project-kicker">How It Helps</p>
              <h2>Support for Everyday Parenting</h2>
              <p>
                Capture short cry samples, follow patterns over time, and use simple insights to support daily care
              </p>
            </div>

            <div className="project-case-grid">
              {data?.use_cases?.map((useCase, index) => (
                <article className="project-case-card" key={useCase.title}>
                  <div className="project-case-media">
                    <img src={useCase.icon} alt="" />
                  </div>
                  <div className="project-case-copy">
                    <h3>{useCase.title}</h3>
                    <p>{useCase.description}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

      </main>

      <Contact data={data?.contact} />
    </div>
  );
};
